import { Fragment, useEffect, useState, type ReactNode } from 'react'
import { AnalyticsConsentCard } from './analytics/AnalyticsConsent'
import { HomepageV3Footer, HomepageV3Header } from './components/HomepageV3Chrome'
import faqMarkdown from './content/faq-v1.md?raw'
import { cartBoxCount } from './order/cartStorage'

type FaqItem = {
  question: string
  answer: string
}

function parseFaqItems(source: string): FaqItem[] {
  const items: FaqItem[] = []
  let currentQuestion = ''
  let currentAnswer: string[] = []

  const pushCurrent = () => {
    if (!currentQuestion) return
    items.push({
      question: currentQuestion,
      answer: currentAnswer.join('\n').trim(),
    })
  }

  for (const line of source.replace(/\r\n/g, '\n').split('\n')) {
    if (line.startsWith('## ')) {
      pushCurrent()
      currentQuestion = line.slice(3).trim()
      currentAnswer = []
      continue
    }
    if (currentQuestion) currentAnswer.push(line)
  }

  pushCurrent()
  return items
}

const FAQ_ITEMS = parseFaqItems(faqMarkdown)

function inlineMarkdown(value: string): ReactNode[] {
  const tokenPattern = /(\[\*\*[^*]+\*\*\]\([^)]+\)|\*\*[^*]+\*\*|`[^`]+`)/g
  const output: ReactNode[] = []
  let cursor = 0
  let match: RegExpExecArray | null

  while ((match = tokenPattern.exec(value)) !== null) {
    if (match.index > cursor) output.push(value.slice(cursor, match.index))

    const token = match[0]
    if (token.startsWith('[**')) {
      const linkMatch = token.match(/^\[\*\*(.+)\*\*\]\((.+)\)$/)
      if (linkMatch) {
        output.push(
          <a href={linkMatch[2].replace('\\:', ':')} key={`${match.index}-${linkMatch[1]}`}>
            <strong>{linkMatch[1]}</strong>
          </a>,
        )
      }
    } else if (token.startsWith('**')) {
      const strongText = token.slice(2, -2)
      if (strongText === 'sugargremlin.co.nz/contact') {
        output.push(
          <a href="/contact" key={`${match.index}-${strongText}`}>
            <strong>{strongText}</strong>
          </a>,
        )
      } else {
        output.push(<strong key={`${match.index}-${strongText}`}>{strongText}</strong>)
      }
    } else {
      const codeText = token.slice(1, -1)
      output.push(<code key={`${match.index}-${codeText}`}>{codeText}</code>)
    }

    cursor = tokenPattern.lastIndex
  }

  if (cursor < value.length) output.push(value.slice(cursor))
  return output
}

function AnswerBlocks({ markdown }: { markdown: string }) {
  const blocks = markdown.trim().split(/\n\s*\n/)

  return (
    <>
      {blocks.map((block, blockIndex) => {
        const lines = block.split('\n').map((line) => line.trimEnd())
        const bulletList = lines.length > 0 && lines.every((line) => line.startsWith('- '))
        const orderedList = lines.length > 0 && lines.every((line) => /^\d+\.\s/.test(line))

        if (bulletList) {
          return (
            <ul key={`ul-${blockIndex}`}>
              {lines.map((line, lineIndex) => (
                <li key={`${blockIndex}-${lineIndex}`}>{inlineMarkdown(line.slice(2))}</li>
              ))}
            </ul>
          )
        }

        if (orderedList) {
          return (
            <ol key={`ol-${blockIndex}`}>
              {lines.map((line, lineIndex) => (
                <li key={`${blockIndex}-${lineIndex}`}>{inlineMarkdown(line.replace(/^\d+\.\s/, ''))}</li>
              ))}
            </ol>
          )
        }

        return (
          <p key={`p-${blockIndex}`}>
            {lines.map((line, lineIndex) => (
              <Fragment key={`${blockIndex}-${lineIndex}`}>
                {inlineMarkdown(line)}
                {lineIndex < lines.length - 1 ? <br /> : null}
              </Fragment>
            ))}
          </p>
        )
      })}
    </>
  )
}

function FaqAccordion() {
  const [openIndex, setOpenIndex] = useState(0)

  return (
    <div className="faq-accordion">
      {FAQ_ITEMS.map((item, index) => {
        const open = openIndex === index
        const questionId = `faq-question-${index}`
        const answerId = `faq-answer-${index}`

        return (
          <article className={`faq-item${open ? ' is-open' : ''}`} key={item.question}>
            <h2>
              <button
                type="button"
                id={questionId}
                aria-expanded={open}
                aria-controls={answerId}
                onClick={() => setOpenIndex((current) => current === index ? -1 : index)}
              >
                <span>{item.question}</span>
                <span className="faq-item__toggle" aria-hidden="true">{open ? '−' : '+'}</span>
              </button>
            </h2>
            {open ? (
              <div className="faq-item__answer" id={answerId} role="region" aria-labelledby={questionId}>
                <AnswerBlocks markdown={item.answer} />
              </div>
            ) : null}
          </article>
        )
      })}
    </div>
  )
}

export default function FaqPage() {
  const [cartCount, setCartCount] = useState(() => cartBoxCount())

  useEffect(() => {
    const refreshCart = () => setCartCount(cartBoxCount())
    window.addEventListener('storage', refreshCart)
    const timer = window.setInterval(refreshCart, 500)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('storage', refreshCart)
    }
  }, [])

  return (
    <div className="sg-v3 faq-page">
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <HomepageV3Header cartCount={cartCount} activeHref="/faq" />

      <main id="main-content" tabIndex={-1}>
        <section className="faq-hero">
          <div className="sg-v3-shell faq-hero__inner">
            <p className="sg-v3-eyebrow">FAQS</p>
            <h1>QUESTIONS? <strong>WE&apos;VE GOT ANSWERS.</strong></h1>
            <p className="faq-hero__intro">Quick answers about ordering, delivery, payments, allergens and everything in between.</p>
          </div>
        </section>

        <section className="faq-body" aria-label="Frequently asked questions">
          <div className="sg-v3-shell faq-body__inner">
            <FaqAccordion />
            <p className="faq-hero__precedence">If anything here differs from one of our formal policies, the formal policy applies.</p>
          </div>
        </section>

        <section className="faq-contact" aria-labelledby="faq-contact-title">
          <div className="sg-v3-shell">
            <div className="faq-contact__card">
              <h2 id="faq-contact-title">Still got a question?</h2>
              <a className="sg-v3-button sg-v3-button--pink" href="/contact">
                CONTACT US <span className="sg-v3-arrow" aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        </section>
      </main>

      <HomepageV3Footer />
      <AnalyticsConsentCard />
    </div>
  )
}
